/**
 * Today's PTE Challenge — mirrors DSAT Daily Challenge pattern.
 * Uses RENDERERS to display the picked question and PTE submit API.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { pteApi } from '@/lib/pteApi';
import RENDERERS from '@/components/pte/PTERenderers';
import { Loader2, Trophy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function PTEDailyChallengeWidget() {
  const [state, setState] = useState({ loading: true });
  const [response, setResponse] = useState({});
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const d = await pteApi.dailyChallenge();
      setState({ loading: false, ...d });
    } catch (e) {
      setState({ loading: false, error: e.response?.data?.detail || e.message });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!state.question) return;
    try {
      const r = await pteApi.submitDaily({
        question_id: state.question.id || state.question.question_id,
        response: response.response ?? null,
      });
      setResult(r);
      if (r.xp_awarded > 0) toast.success(`+${r.xp_awarded} XP awarded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
  };

  if (state.loading) {
    return (
      <div className="rounded-2xl bg-white border border-slate-200 p-6 flex items-center gap-2 text-slate-500"
        data-testid="pte-daily-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading today's challenge…
      </div>
    );
  }
  if (state.error) {
    return <div className="rounded-2xl bg-white border border-red-200 p-6 text-red-600" data-testid="pte-daily-error">{state.error}</div>;
  }
  const q = state.question;
  const Renderer = q && RENDERERS[q.question_type];

  return (
    <div className="rounded-2xl bg-gradient-to-br from-emerald-900 to-slate-900 text-white p-6 md:p-8"
      data-testid="pte-daily-widget">
      <div className="flex items-center gap-2 mb-2">
        <Trophy className="h-5 w-5 text-emerald-300" />
        <div className="text-xs uppercase tracking-widest text-emerald-300">Today's PTE Challenge</div>
      </div>
      <div className="text-2xl font-bold mb-3">{state.meta?.label || 'Daily task'}</div>
      {state.already_completed_today ? (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-200 text-sm mb-3">
          <CheckCircle2 className="h-4 w-4" /> Completed today. Come back tomorrow.
        </div>
      ) : null}
      <div className="bg-white text-slate-800 rounded-xl overflow-hidden">
        {Renderer ? <Renderer question={q} onResponseChange={setResponse} disabled={!!result} /> : null}
      </div>
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-emerald-200/80">
          Sign in to earn <strong>+5 XP</strong> for a correct answer.
        </div>
        {result ? (
          <button onClick={load}
            data-testid="pte-daily-reload"
            className="px-4 py-2 rounded-full bg-emerald-400 hover:bg-emerald-300 text-black font-semibold text-sm">
            Refresh
          </button>
        ) : (
          <button onClick={submit}
            data-testid="pte-daily-submit"
            className="px-6 py-2.5 rounded-full bg-emerald-400 hover:bg-emerald-300 text-black font-bold text-sm">
            Submit answer
          </button>
        )}
      </div>
      {result && (
        <div className="mt-3 text-sm text-emerald-100" data-testid="pte-daily-feedback">
          {result.result?.feedback} · <span className="font-mono text-xs">{result.result?.score ?? '—'}/{result.result?.max ?? '—'}</span>
        </div>
      )}
    </div>
  );
}
